const term = (value: string) => value;

/**
 * App-specific UI copy (WebProductCatalog).
 * English source strings — prefixed in texts.ts via addProjectPrefix.
 * Shared footer/language strings live in WebCommon (see texts.ts).
 */
export const uiTexts = {
  BrandName: term('JABLOTRON'),
  NavProducts: term('Products'),
  NavAdmin: term('Admin'),
  NavBlog: term('Blog'),
  HeroTitle: term('We protect.'),
  HeroSubtitle: term(
    'JABLOTRON security systems and smart home. Reliable protection for houses, apartments and businesses.',
  ),
  HeroSearchHint: term('{{count}} products, parameters, manuals and wiring diagrams. Search by SKU or name.'),
  HeroSuggestedLabel: term('Most searched products:'),
  CategoriesTitle: term('Product lines'),
  CategoriesSubtitle: term('Choose a system by building type, or start from a specific need.'),
  FeaturesTitle: term('What we offer'),
  NewsTitle: term("What's new in the catalog"),
  NewsSubtitle: term('New control panels, detectors and accessories just added to the range.'),
  NewsCatalogCount: term('products for security and automation'),
  NewsBrowseCatalog: term('Browse the catalog'),
  ShowProducts: term('Show products'),
  ShowLine: term('Show'),
  FooterCopyright: term('© JABLOTRON. All rights reserved.'),
  ProductRelated: term('Related products'),
  ProductSpecs: term('Specifications'),
  ProductAbout: term('About the product'),
  ProductInstallation: term('Installation'),
  ProductDownloads: term('Downloads'),
  ProductFeatures: term('Features'),
  CatalogAllProducts: term('All products'),
  NotFoundHeading: term('This page could not be found.'),
  NotFoundLead: term('The link is outdated or the address has a typo. Check the product code, or search for it.'),
  NotFoundSearchPlaceholder: term('Product code or name, e.g. JA-103K'),
  NotFoundLinesHeading: term('Or start from a product line'),
  NotFoundInvalidAddress: term('Invalid address'),
  NotFoundReportBrokenLink: term('Report a broken link'),
  NotFoundReportSubject: term('Broken link in the product catalog'),
  NotFoundReportBody: term("This link doesn't work:"),
  BackHome: term('Back to home'),
  SearchPlaceholder: term('Search products (JA-103K or ja103k)'),
  FilterListPlaceholder: term('Filter the list (code or name)'),
  SearchClear: term('Clear search'),
  SearchClose: term('Close search'),
  SearchNoResults: term('No results found.'),
  SearchProducts: term('Products'),
  FilterWireless: term('Wireless'),
  FilterWired: term('Wired'),
  LabelWireless: term('Wireless'),
  LabelWired: term('Wired'),
  LabelHybrid: term('Hybrid'),
  ConnectionType: term('Connection type'),
  SpecDimensions: term('Dimensions'),
  SpecPowerSupply: term('Power supply'),
  SpecRatedDraw: term('Rated draw'),
  SpecPeakDraw: term('Peak draw'),
  NoProductsMatch: term('No products match the filters.'),
  NoProductsInSubcategory: term('There are no products in this category yet.'),
  FilterLocation: term('Location'),
  FilterColor: term('Color'),
  FilterPanelCompatibility: term('Panel compatibility'),
  FilterConnectionLabel: term('Connection'),
  FilterAvailability: term('Availability'),
  FilterAvailabilityAvailable: term('Available'),
  ViewGrid: term('Grid view'),
  ViewList: term('List view'),
  LocationIndoor: term('Indoor'),
  LocationOutdoor: term('Outdoor'),
  LabelPanel: term('Panel'),
  FilterReset: term('Reset'),
  FilterRemove: term('Remove filter'),
  FilterValueUnavailable: term('No product matches the selected filters'),
  ColorWhite: term('White'),
  ColorGray: term('Gray'),
  ColorAnthracite: term('Anthracite'),
  ColorBrown: term('Brown'),
  ColorBlack: term('Black'),
  ColorRed: term('Red'),
  ColorSteel: term('Steel'),
  ShowMore: term('Show more'),
  ShowLess: term('Show less'),
  ShowAllCount: term('Show all {{count}}'),
  BreadcrumbHome: term('Home'),
  BreadcrumbLabel: term('Breadcrumb'),
  KeySpecs: term('Key specifications'),
  BasicDesc: term('Basic description'),
  WhyChooseProduct: term('Why choose this product'),
  TechSpecs: term('Technical specifications'),
  InstallUnavailable: term('Installation instructions are not available.'),
  NoDownloads: term('No files to download.'),
  NoRelated: term('No related products.'),
  ColorVariants: term('Color variants'),
  ConnectionVariants: term('Connection type'),
  WiredVariant: term('Wired'),
  WirelessVariant: term('Wireless'),
  Compatibility: term('Compatibility'),
  Certification: term('Certification'),
  ProductLine: term('Product line'),
  CheckCompatibility: term('Check compatibility'),
  FaqTitle: term('Frequently asked questions'),
  PackageContentsTitle: term('Package contents'),
  MontageConsiderationsTitle: term('What to keep in mind during installation'),
  InstallationGuideTitle: term('How to install it correctly'),
  InstallationVideoTitle: term('Installation video'),
  PlayVideo: term('Play video'),
  InstallationTipsTitle: term('Things to watch out for when designing the system'),
  DownloadFile: term('Download'),
  DownloadAll: term('Download all'),
  ProductAccessories: term('Accessories'),
  SearchCategories: term('Categories'),
  SearchCodeHint: term('Codes work with or without hyphens (JA-103K = ja103k).'),
  SearchResultsTitle: term('Search results'),
  SearchResultsFor: term('Results for „{{query}}“'),
  SearchResultsEmptyQuery: term('Enter a search term in the header to find products and categories.'),
  SearchResultsCount: term('{{count}} products'),
  SearchShowAll: term('Show all results'),
  SearchSubmit: term('Search'),
  SearchPagePrev: term('Previous'),
  SearchPageNext: term('Next'),
  SearchPageStatus: term('Page {{page}} / {{pages}}'),
  MenuOpen: term('Open catalog menu'),
  MenuClose: term('Close catalog menu'),
  MobileMenuTitle: term('Menu'),
  MobileMenuViewProductLine: term('View the product line'),
  MegaMenuCategoriesHeading: term('Product-type categories'),
  MegaMenuLinesHeading: term('Product line'),
  MegaMenuCategoryControlPanels: term('Control units'),
  MegaMenuTaglineFlexiAlarm: term('Professional protection'),
  MegaMenuTaglineMercuryAlarm: term('New generation'),
  MegaMenuTaglineNanny: term('Child safety'),
  MegaMenuTaglineMobileControl: term('Smart home'),
  MegaMenuTaglineGrade3: term('Certified security, grade 3'),
  MegaMenuTaglineSoftwareAndApplications: term('Apps and cloud tools'),
  SidebarSectionCoreSystem: term('Core system'),
  SidebarSectionControl: term('Control'),
  SidebarSectionDetection: term('Detection'),
  SidebarSectionVideo: term('Video'),
  SidebarSectionAutomation: term('Automation'),
  /** Distinct from ProductInstallation's 'Installation' term to avoid colliding with its "Montáž" translation. */
  SidebarSectionInstallation: term('Installation section'),
  SidebarSectionSoftware: term('Software'),
  /** Distinct from subcategoryPages.1_14/2_16's 'Installation accessories' title to avoid colliding with that translation. */
  CategoryInstallationAccessoriesHeading: term('Installation accessories row'),
  /** Czech needs three plural forms; 5+ reuses SearchResultsCount ('{{count}} produktů'), hence the odd English for 2–4. */
  CategoryElementCountOne: term('{{count}} product'),
  CategoryElementCountFew: term('{{count}} products in category'),
  CategoryElementComingSoon: term('In preparation'),
  CategoryElementChooseControlPanel: term('Choose a control panel'),
  FooterPartnersHeading: term('For installation partners'),
  FooterUsefulInfoHeading: term('Useful information'),
  FooterPartnerOfficialWebsite: term('Official website'),
  FooterPartnerMyCompanyApp: term('MyCOMPANY app'),
  FooterPartnerEshop: term('JABLOTRON eshop'),
  FooterPartnerZone: term('Partner zone'),
  FooterProductCompatibility: term('Product compatibility'),
  FooterDataAct: term('Data act'),
  FooterCountry: term('Czech Republic'),
  FooterIdLabel: term('ID No.:'),
  FooterTinLabel: term('TIN:'),
  CompatibilityFilterPanelTitle: term('Filtering'),
  CompatibilityFilterSystemLabel: term('System'),
  CompatibilitySystemHundredPlus: term('JABLOTRON 100+'),
  CompatibilitySystemMercury: term('JABLOTRON Mercury'),
  CompatibilityLabelBus: term('Bus connection'),
  CompatibilityStatusCompatible: term('Compatible'),
  CompatibilityStatusIncompatible: term('Incompatible'),
  CompatibilityStatusUnverified: term('Not yet verified'),
  CompatibilitySortByPanelAriaLabel: term('Sort by compatibility'),
  CompatibilityPanelNotInCatalog: term('No longer sold as a separate product'),
  CompatibilitySortByDeviceAriaLabel: term('Sort by device'),
  CompatibilityColumnProduct: term('Product'),
  ProductDiscontinuedBadge: term('Discontinued'),
  PostBreadcrumbBlog: term('Blog'),
  PostTocTitle: term('Table of contents'),
  PostCategoriesTitle: term('Article categories'),
  PostOnlyInEnglish: term('This article is only available in English.'),
  PostOnlyInCzech: term('This article is only available in Czech.'),
  BlogCategoryAll: term('All categories'),
  BlogCategoryNews: term('News and info'),
  BlogCategoryCaseStudies: term('Case studies'),
  BlogCategoryJabWiki: term('JAB wiki'),
  BlogCategoryProductMaps: term('Product maps'),
  BlogNoPosts: term('No articles found.'),
  BlogMissingArticle: term('The article “{{title}}” is not available in English.'),
  BlogMissingArticleLanguages: term('It is available in the following languages:'),
  BlogLatestTitle: term('Latest articles'),
  BlogSeeAll: term('See all articles'),
  CompatibilityBannerTitle: term('Not sure whether a device works with your control panel?'),
  CompatibilityBannerText: term(
    'The compatibility table shows which peripherals each JABLOTRON control panel supports.',
  ),
  CompatibilityBannerCta: term('Compatibility table'),
  AppTileMore: term('More about the app'),
  AppTileListingTitle: term('Our apps'),
  AppStepsBeforeYouStart: term('Before you start'),
} as const;
